import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import { getProductsInWarehouse, selectProductsInWarehouse, selectProductsInWarehouseLoading, selectSelectedWarehouse, selectWarehouseDetailLoading } from "../../redux/warehouse/warehouseSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import { closeModal } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";
import ListPagination from "../templates/ListPagination";

export default function WarehouseProducts({ warehouseId }) {
    const dispatch = useDispatch();
    const warehouse = useSelector(selectSelectedWarehouse);
    const loading = useSelector(selectWarehouseDetailLoading);
    const productsData = useSelector(selectProductsInWarehouse);
    const productsLoading = useSelector(selectProductsInWarehouseLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    
    const [searchTerm, setSearchTerm] = useState('');
    
    // Carica i prodotti iniziali
    useEffect(() => {
        if (warehouseId && accountData?._id) {
            handlePageUpdate(0, 10);
        }
    }, [warehouseId, accountData]);
    
    // Funzione per la paginazione
    const handlePageUpdate = (start, length) => {
        dispatch(getProductsInWarehouse({
            accountId: accountData?._id,
            warehouseId,
            start,
            length,
            search: searchTerm.length >= 3 ? searchTerm : '',
            token
        }));
    };
    
    // Gestisce la ricerca
    useEffect(() => {
        if (warehouseId && accountData?._id) {
            const timer = setTimeout(() => {
                handlePageUpdate(0, 10);
            }, 500);
            
            return () => clearTimeout(timer);
        }
    }, [searchTerm]);
    
    // Formatta il prezzo
    const formatPrice = (price) => {
        if (price === undefined || price === null) return "--";
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);
    };
    
    if (loading) {
        return <LoadingSection />;
    }
    
    if (!warehouse) {
        return (
            <div className="p-4">
                <p className="text-gray-500">Nessun magazzino selezionato</p>
            </div>
        );
    }
    
    const hasProducts = productsData?.data?.some(p => p.variants?.some(v => v.warehouseId === warehouseId));

    return (
        <div className="p-4 h-full flex flex-col">
            {/* Header con titolo e pulsante chiudi */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                    Prodotti nel magazzino: {warehouse.name}
                </h2>
                <button
                    className="secondary"
                    onClick={() => dispatch(closeModal())}
                >
                    <DynamicIcon iconClass="fas fa-times" /> Chiudi
                </button>
            </div>

            {/* Barra di ricerca */}
            <div className="mb-4">
                <div className="relative">
                    <input
                        type="text"
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pe-10 p-2.5"
                        placeholder="Cerca prodotto..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="absolute inset-y-0 end-0 flex items-center pe-3 pointer-events-none">
                        <DynamicIcon iconClass="fa-solid fa-search text-gray-400" />
                    </div>
                    {searchTerm && (
                        <button
                            className="absolute inset-y-0 end-8 flex items-center pe-3"
                            onClick={() => setSearchTerm('')}
                        >
                            <DynamicIcon iconClass="fa-solid fa-times" />
                        </button>
                    )}
                </div>
            </div>

            {/* Lista prodotti */}
            <div className="flex-1 overflow-hidden flex flex-col">
                {productsLoading && <LoadingSection />}

                {!productsLoading && !hasProducts ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-12">
                        <DynamicIcon iconClass="fa-solid fa-boxes-stacked text-4xl mb-3" />
                        <p className="text-lg">Nessun prodotto presente in questo magazzino</p>
                    </div>
                ) : (
                    <ListPagination
                        initTotalElements={productsData?.recordsFiltered || 0}
                        onPageChange={handlePageUpdate}
                    >
                        <div className="w-full text-sm flex flex-col gap-2">
                            {/* Headers */}
                            <div className="w-full flex justify-between font-bold">
                                <div className="px-4 py-3 w-1/5">CODICE</div>
                                <div className="px-4 py-3 w-1/4">DESCRIZIONE</div>
                                <div className="px-4 py-3 w-1/5">POSIZIONE</div>
                                <div className="px-4 py-3 w-1/6 text-right">QUANTITÀ</div>
                                <div className="px-4 py-3 w-1/6 text-right">PREZZO</div>
                            </div>

                            {/* Righe prodotti */}
                            {productsData?.data?.map((product, index) => {
                                const warehouseVariants = product.variants?.filter(v => v.warehouseId === warehouseId);

                                if (!warehouseVariants || warehouseVariants.length === 0) return null;

                                return warehouseVariants.map((variant, variantIndex) => {
                                    const position = warehouse.positions?.find(p => p._id === variant.warehousePositionId);

                                    return (
                                        <div
                                            key={`${product._id}-${variant._id || variantIndex}`}
                                            className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} w-full flex justify-between rounded-lg`}
                                        >
                                            <div className="px-4 py-3 w-1/5 truncate">
                                                <div className="font-semibold">{product.code || "--"}</div>
                                                {variant.barcode && (
                                                    <div className="text-xs text-gray-500">Barcode: {variant.barcode}</div>
                                                )}
                                                {variant.serialnumber && (
                                                    <div className="text-xs text-gray-500">S/N: {variant.serialnumber}</div>
                                                )}
                                            </div>
                                            <div className="px-4 py-3 w-1/4 truncate">
                                                <div>{product.title || "--"}</div>
                                                {variant.brandLabel && (
                                                    <div className="text-xs text-gray-500">{variant.brandLabel}</div>
                                                )}
                                            </div>
                                            <div className="px-4 py-3 w-1/5 truncate">
                                                {position ? position.name : "--"}
                                                {position && position.code && (
                                                    <div className="text-xs text-gray-500">Cod: {position.code}</div>
                                                )}
                                            </div>
                                            <div className="px-4 py-3 w-1/6 text-right">
                                                {variant.quantity || "0"}
                                            </div>
                                            <div className="px-4 py-3 w-1/6 text-right">
                                                {formatPrice(variant.sellPrice || product.sellPrice)}
                                            </div>
                                        </div>
                                    );
                                });
                            }).flat().filter(Boolean)}
                        </div>
                    </ListPagination>
                )}
            </div>
        </div>
    );
}
